import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

interface VisitModalActionContextType {
  action: ReactNode | null;
  setAction: (action: ReactNode | null) => void;
  // A second, independent footer slot for the always-visible "Dodaj do wizyty"
  // button, so it can coexist with the per-tab `action` (save/cancel, export…).
  addAction: ReactNode | null;
  setAddAction: (action: ReactNode | null) => void;
  // The "Podsumowanie wizyty" entry lives in the Dodaj-do-wizyty menu (rendered
  // by VisitAddMenu), but its logic (PDF generation / attach / download + the
  // confirm dialog) lives in OrganizationVisitModalTabs. The owner publishes
  // availability + registers a handler here; the menu reads/triggers it.
  summaryAvailable: boolean;
  setSummaryAvailable: (available: boolean) => void;
  registerSummaryHandler: (handler: (() => void) | null) => void;
  triggerSummary: () => void;
}

const VisitModalActionContext =
  createContext<VisitModalActionContextType | null>(null);

interface VisitModalActionProviderProps {
  children: ReactNode;
}

export function VisitModalActionProvider({
  children,
}: VisitModalActionProviderProps) {
  const [action, setActionState] = useState<ReactNode | null>(null);
  const [addAction, setAddActionState] = useState<ReactNode | null>(null);
  const [summaryAvailable, setSummaryAvailableState] = useState(false);
  const summaryHandlerRef = useRef<(() => void) | null>(null);

  const setAction = useCallback(
    (newAction: ReactNode | null) => setActionState(newAction),
    [],
  );

  const setAddAction = useCallback(
    (newAction: ReactNode | null) => setAddActionState(newAction),
    [],
  );

  const setSummaryAvailable = useCallback(
    (available: boolean) => setSummaryAvailableState(available),
    [],
  );

  const registerSummaryHandler = useCallback(
    (handler: (() => void) | null) => {
      summaryHandlerRef.current = handler;
    },
    [],
  );

  const triggerSummary = useCallback(() => {
    summaryHandlerRef.current?.();
  }, []);

  const value = useMemo(
    () => ({
      action,
      setAction,
      addAction,
      setAddAction,
      summaryAvailable,
      setSummaryAvailable,
      registerSummaryHandler,
      triggerSummary,
    }),
    [
      action,
      setAction,
      addAction,
      setAddAction,
      summaryAvailable,
      setSummaryAvailable,
      registerSummaryHandler,
      triggerSummary,
    ],
  );

  return (
    <VisitModalActionContext.Provider value={value}>
      {children}
    </VisitModalActionContext.Provider>
  );
}

export function useVisitModalAction() {
  const context = useContext(VisitModalActionContext);
  if (!context) {
    throw new Error(
      'useVisitModalAction must be used within VisitModalActionProvider',
    );
  }
  return context;
}
